import { API, Auth, Hub } from "aws-amplify";
import {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  getUser,
  GetPodSession,
  ListOrganizations,
  PodQueueSessionsByStatus,
  PodSessionsByProctor,
  PodSessionsByTransferToProctor,
} from "../graphql/queries";
import {
  claimPodSessionAndNotify,
  fetchOrCreateUser,
  fetchPodSessionClaimState,
  PodSessionAlreadyClaimedError,
} from "../Components/PodV2/api";
import {
  bindGraphqlSubscription,
  claimedIdsSignature,
  isQueueRowClaimed,
  queueRowDisplayProps,
  queueRowProps,
  readMissingQueueSessions,
} from "../Components/PodV2/podQueueList";
import {
  QueueClaimButton,
  QueueDataTable,
  QueueRowHighlightStyle,
  queueCompactTablePad,
  waitingColumn,
  withQueueRowHighlight,
} from "../Components/PodQueue/QueueSessionList";
import {
  replayQueueEvents,
  reducePodQueueState,
  UrgentQueueButton,
  UrgentQueuePanel,
  UrgentQueuePopup,
  shouldShowUrgentPanel,
  urgentSessionsFromDisplayed,
} from "../Components/PodQueue/urgentQueue";
import { useClaimConfirmations } from "../Components/PodQueue/useClaimConfirmations";
import {
  getEmailUsername,
  getProctorUsername,
  isUnclaimedProctor,
} from "../Components/PodV2/sessionUtils";
import { CreatePodSession } from "../graphql/mutations";
import {
  Box,
  ResponsiveContext,
  Text,
  DataTable,
  Button,
  Data,
  Toolbar,
  DataSearch,
  DataSummary,
  Layer,
  Form,
  FormField,
  TextInput,
  Select,
} from "grommet";
import {
  onLitePodSessionCreate,
  onPodSessionClaim,
  onPopToTopOfPod,
  onUpdatePodSession,
} from "../graphql/subscriptions";
import { Transaction } from "grommet-icons";
import PodQueueSecondaries from "../Components/PodQueue/PodQueueSecondaries";
import PodQueueCollegeBoard from "../Components/PodQueue/PodQueueCollegeBoard";
import { buildNotTawkOrganizationIds } from "../Components/PodQueue/tawkSession";
import addIcon from "../assets/icons/add.svg";
import proctorIcon from "../assets/icons/proctor.svg";
import secondariesIcon from "../assets/icons/secondaries.svg";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const ICON_MASK_COLOR = "rgb(26 4 4 / 75%)";

const createMaskedIcon = (iconSrc, backgroundColor = ICON_MASK_COLOR) => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-block",
      width: "23px",
      height: "23px",
      flexShrink: 0,
      backgroundColor,
      WebkitMaskImage: `url(${iconSrc})`,
      maskImage: `url(${iconSrc})`,
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
      maskPosition: "center",
      WebkitMaskSize: "contain",
      maskSize: "contain",
    }}
  />
);

const addSessionIcon = createMaskedIcon(addIcon, "#FFFFFF");
const mySessionsIcon = createMaskedIcon(proctorIcon);
const mySessionsIconActive = createMaskedIcon(proctorIcon, "#FFFFFF");
const secondariesSessionIcon = createMaskedIcon(secondariesIcon);
const secondariesSessionIconActive = createMaskedIcon(
  secondariesIcon,
  "#FFFFFF",
);

const CENTRAL_TZ = "America/Chicago";
const VALID_POD_IDS = new Set(["1", "2", "3", "4", "5", "6", "7"]);
const STANDARD_POD_IDS = new Set(["1", "2", "3", "4", "6", "7"]);
const CONTROL_HEIGHT = "44px";
const controlHeightStyle = {
  height: CONTROL_HEIGHT,
  minHeight: CONTROL_HEIGHT,
  maxHeight: CONTROL_HEIGHT,
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
const buttonControlStyle = {
  ...controlHeightStyle,
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.14)",
};

function getOrganizationPodId(organization) {
  const podId = String(
    organization?.pod_id ?? organization?.podId ?? "",
  ).trim();
  return VALID_POD_IDS.has(podId) ? podId : null;
}

function normalizeSessionPodId(session) {
  const raw = session?.podId ?? session?.podID;
  if (raw == null) return "";
  return String(raw).trim();
}

function isAlaCartePodId(normalizedPodId) {
  return normalizedPodId === "5" || !STANDARD_POD_IDS.has(normalizedPodId);
}

function getCentralDateString(date = new Date()) {
  return dayjs(date).tz(CENTRAL_TZ).format("YYYY-MM-DD");
}

const emptyForm = {
  testerName: "",
  testerEmail: "",
  testerPhone: "",
  organization: null,
};

const podOptions = [
  { label: "All Pods", value: "all" },
  { label: "Pod A", value: "podA" },
  { label: "Pod B", value: "podB" },
  { label: "Pod C", value: "podC" },
  { label: "Pod D", value: "podD" },
  { label: "Pod E", value: "podE" },
  { label: "Pod F", value: "podF" },
  { label: "A la carte", value: "alacarte" },
];

const podViewOptions = [
  { label: "My Sessions", value: "mySessions" },
  { label: "Secondaries", value: "secondaries" },
  { label: "College Board", value: "collegeBoard" },
];

const createColumns = ({
  showTransferredIndicator = false,
  showCreatedAtCentralDate = false,
  claimAction = false,
  compactColumns = false,
  claimingId = null,
  claimedIds = new Set(),
  notTawkOrganizationIds,
  onClaimSession,
  proctorUsernames = [],
} = {}) => {
  const textColumnSize = compactColumns ? "small" : "medium";
  const columns = [
    {
      property: "actions",
      header: <Text>Actions</Text>,
      render: (datum) =>
        claimAction ? (
          <QueueClaimButton
            session={datum}
            claimed={isQueueRowClaimed(claimedIds, datum.id)}
            claiming={claimingId === datum.id}
            notTawkOrganizationIds={notTawkOrganizationIds}
            onClaim={onClaimSession}
          />
        ) : (
          <Box direction="row">
            <Button
              label="View"
              size="small"
              primary
              onClick={() =>
                window.open(`/pod-session-v2?id=${datum.id}`, "_blank")
              }
            />
          </Box>
        ),
      align: "start",
      alignVertical: "middle",
      size: "small",
    },
  ];

  if (claimAction) {
    columns.push(waitingColumn({ claimedIds }));
  }

  columns.push(
    {
      property: "testerName",
      header: <Text>Tester Name</Text>,
      render: (datum) => (
        <Box direction="row" align="center" gap="xsmall">
          {showTransferredIndicator &&
            proctorUsernames.includes(datum.transferToProctor) && (
              <Transaction size="small" color="brand" a11yTitle="Transferred" />
            )}
          <Text size="small" truncate>
            {datum.testerName}
          </Text>
        </Box>
      ),
      align: "start",
      alignVertical: "middle",
      size: textColumnSize,
    },
    {
      property: "testerEmail",
      header: <Text>Tester Email</Text>,
      render: (datum) => (
        <Box direction="row">
          <Text size="small" truncate>
            {datum.testerEmail}
          </Text>
        </Box>
      ),
      align: "start",
      alignVertical: "middle",
      size: textColumnSize,
    },
    {
      property: "organizationName",
      header: <Text>Organization Name</Text>,
      render: (datum) => (
        <Box direction="row">
          <Text size="small" truncate>
            {datum.organizationName}
          </Text>
        </Box>
      ),
      align: "start",
      alignVertical: "middle",
      size: textColumnSize,
    },
  );

  if (showCreatedAtCentralDate) {
    columns.push({
      property: "createdAtCentralDate",
      header: <Text>Date</Text>,
      render: (datum) => (
        <Box direction="row">
          <Text size="small" truncate>
            {datum.createdAtCentralDate || "—"}
          </Text>
        </Box>
      ),
      align: "start",
      alignVertical: "middle",
      size: "small",
    });
  }

  return columns;
};

export default function PodQueue() {
  const [queueState, setQueueState] = useState({
    sessions: [],
    claimedIds: new Set(),
    pendingClaims: new Map(),
    claimVersions: new Map(),
    readRequests: [],
    loaded: false,
  });
  const allPodSessions = queueState.sessions;
  const claimedIds = queueState.claimedIds;
  const queueStateRef = useRef(queueState);
  queueStateRef.current = queueState;
  const rememberQueueEvent = useCallback((event) => {
    if (event?.claimGeneration != null) {
      queueEventsRef.current.push(event);
    }
    setQueueState((current) => reducePodQueueState(current, event));
  }, []);

  const consumeReadRequests = useCallback((done) => {
    setQueueState((current) => ({
      ...current,
      readRequests: (current.readRequests ?? []).filter(
        (item) =>
          !done.some(
            (finished) =>
              finished.id === item.id &&
              finished.generation === item.generation,
          ),
      ),
    }));
  }, []);

  useClaimConfirmations({
    readRequests: queueState.readRequests,
    onSession: rememberQueueEvent,
    onConsumed: consumeReadRequests,
  });
  const [selectedPod, setSelectedPod] = useState("all");
  const [urgentOpen, setUrgentOpen] = useState(false);
  const screenSize = useContext(ResponsiveContext);
  const compactQueue = screenSize === "small" || screenSize === "xsmall";
  const [myPodSessions, setMyPodSessions] = useState([]);
  const [mySessionsProctorUsernames, setMySessionsProctorUsernames] = useState(
    [],
  );
  const [secondariesMounted, setSecondariesMounted] = useState(false);
  const [collegeBoardMounted, setCollegeBoardMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMySessions, setLoadingMySessions] = useState(false);
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [submittingSession, setSubmittingSession] = useState(false);
  const [createSessionError, setCreateSessionError] = useState(null);
  const [formValues, setFormValues] = useState(emptyForm);
  const [claimingId, setClaimingId] = useState(null);
  const [claimNotice, setClaimNotice] = useState(null);
  const [proctorUsername, setProctorUsername] = useState(null);
  const queueEventsRef = useRef([]);
  const queueFetchRef = useRef(null);
  const queueReconcileAgainRef = useRef(false);

  const getCurrentProctorUsernames = async () => {
    const cognitoUser = await Auth.currentUserInfo();
    const email = cognitoUser.attributes.email;
    const legacyUsername = getEmailUsername(email);

    const { data } = await API.graphql({
      query: getUser,
      variables: { id: cognitoUser.id },
    });

    return [
      ...new Set(
        [getProctorUsername(data.getUser, email), legacyUsername].filter(
          Boolean,
        ),
      ),
    ];
  };

  const fetchMyPodSessions = useCallback(async () => {
    const proctors = await getCurrentProctorUsernames();

    const fetchPaginatedSessions = async (query, resultKey, variables) => {
      let sessions = [];
      let token = null;
      let refetches = 0;

      do {
        const { data } = await API.graphql({
          query,
          variables: {
            ...variables,
            sortDirection: "DESC",
            nextToken: token,
            limit: 300,
          },
        });

        const newSessions = data[resultKey].items.map((session) => ({
          ...session,
          podId: session.podID ?? session.podId,
        }));
        token = data[resultKey].nextToken;
        sessions = [...sessions, ...newSessions];
        refetches++;
      } while (token && refetches < 4);

      return sessions;
    };

    const sessionResults = await Promise.all(
      proctors.flatMap((proctor) => [
        fetchPaginatedSessions(PodSessionsByProctor, "podSessionByProctor", {
          proctor,
        }),
        fetchPaginatedSessions(
          PodSessionsByTransferToProctor,
          "podSessionByTransferToProctor",
          { transferToProctor: proctor },
        ),
      ]),
    );

    const sessionsById = new Map();
    sessionResults.flat().forEach((session) => {
      sessionsById.set(session.id, session);
    });

    return {
      sessions: [...sessionsById.values()].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      ),
      proctors,
    };
  }, []);

  const handleMySessions = useCallback(async () => {
    setLoadingMySessions(true);
    try {
      const { sessions, proctors } = await fetchMyPodSessions();
      setMyPodSessions(sessions);
      setMySessionsProctorUsernames(proctors);
      setSelectedPod("mySessions");
    } catch (err) {
      console.error("Error fetching my pod sessions:", err);
    } finally {
      setLoadingMySessions(false);
    }
  }, [fetchMyPodSessions]);

  const podDropdownOptions = useMemo(
    () => (compactQueue ? [...podOptions, ...podViewOptions] : podOptions),
    [compactQueue],
  );

  const handlePodDropdownChange = useCallback(
    (value) => {
      if (value === "mySessions") {
        handleMySessions();
        return;
      }
      if (value === "secondaries") {
        setSecondariesMounted(true);
        setSelectedPod("secondaries");
        return;
      }
      if (value === "collegeBoard") {
        setCollegeBoardMounted(true);
        setSelectedPod("collegeBoard");
        return;
      }
      setSelectedPod(value);
    },
    [handleMySessions],
  );

  const openSecondaries = useCallback(() => {
    setSecondariesMounted(true);
    setSelectedPod("secondaries");
  }, []);

  const openCollegeBoard = useCallback(() => {
    setCollegeBoardMounted(true);
    setSelectedPod("collegeBoard");
  }, []);

  const notTawkOrganizationIds = useMemo(
    () => buildNotTawkOrganizationIds(organizations),
    [organizations],
  );

  const fetchOrganizations = useCallback(async () => {
    setLoadingOrganizations(true);
    try {
      const { data } = await API.graphql({ query: ListOrganizations });
      const parsed = JSON.parse(data.listOrganizations);
      const items = Array.isArray(parsed.body) ? parsed.body : [];
      const sorted = [...items].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      );
      setOrganizations(sorted);
      setOrganizationOptions(sorted);
    } catch (err) {
      console.error("Error fetching organizations:", err);
      setOrganizations([]);
      setOrganizationOptions([]);
    } finally {
      setLoadingOrganizations(false);
    }
  }, []);

  const handleOpenAddSessionModal = () => {
    setFormValues(emptyForm);
    setCreateSessionError(null);
    setShowAddSessionModal(true);
    fetchOrganizations();
  };

  const handleCloseAddSessionModal = () => {
    setShowAddSessionModal(false);
    setFormValues(emptyForm);
    setCreateSessionError(null);
    setOrganizationOptions(organizations);
  };

  const handleOrganizationSearch = (searchTerm) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      setOrganizationOptions(organizations);
      return;
    }

    setOrganizationOptions(
      organizations.filter((organization) =>
        (organization.name || "").toLowerCase().includes(normalizedSearch),
      ),
    );
  };

  const handleCreateSession = async ({ value }) => {
    const { testerName, testerEmail, testerPhone, organization } = value;

    if (!organization?.id) {
      return;
    }

    const podID = getOrganizationPodId(organization);
    if (!podID) {
      setCreateSessionError(
        "Selected organization is missing a valid pod assignment (1-7).",
      );
      return;
    }

    const sessionId = uuidv4();
    setSubmittingSession(true);
    setCreateSessionError(null);

    try {
      const { data } = await API.graphql({
        query: CreatePodSession,
        variables: {
          input: {
            id: sessionId,
            type: "PodSession",
            testerName,
            testerEmail,
            testerPhone,
            status: "QUEUE",
            organizationId: organization.id,
            organizationName: organization.name,
            podID,
            createdAtCentralDate: getCentralDateString(),
          },
        },
      });

      const createdSession = {
        ...data.createPodSession,
        podId: data.createPodSession.podID,
      };

      const createEvent = { type: "create", session: createdSession };
      queueEventsRef.current.push(createEvent);
      rememberQueueEvent(createEvent);
      handleCloseAddSessionModal();
      window.open(`/pod-session-v2?id=${sessionId}`, "_blank");
    } catch (err) {
      console.error("Error creating pod session:", err);
      setCreateSessionError("Failed to create session. Please try again.");
    } finally {
      setSubmittingSession(false);
    }
  };

  const podASessions = useMemo(() => {
    return allPodSessions.filter(
      (session) => normalizeSessionPodId(session) === "1",
    );
  }, [allPodSessions]);
  const podBSessions = useMemo(() => {
    return allPodSessions.filter(
      (session) => normalizeSessionPodId(session) === "2",
    );
  }, [allPodSessions]);
  const podCSessions = useMemo(() => {
    return allPodSessions.filter(
      (session) => normalizeSessionPodId(session) === "3",
    );
  }, [allPodSessions]);
  const podDSessions = useMemo(() => {
    return allPodSessions.filter(
      (session) => normalizeSessionPodId(session) === "4",
    );
  }, [allPodSessions]);
  const podESessions = useMemo(() => {
    return allPodSessions.filter(
      (session) => normalizeSessionPodId(session) === "6",
    );
  }, [allPodSessions]);
  const podFSessions = useMemo(() => {
    return allPodSessions.filter(
      (session) => normalizeSessionPodId(session) === "7",
    );
  }, [allPodSessions]);
  const alacarteSessions = useMemo(() => {
    return allPodSessions.filter((session) =>
      isAlaCartePodId(normalizeSessionPodId(session)),
    );
  }, [allPodSessions]);

  const queueSessions = useMemo(() => {
    switch (selectedPod) {
      case "all":
        return allPodSessions;
      case "podA":
        return podASessions;
      case "podB":
        return podBSessions;
      case "podC":
        return podCSessions;
      case "podD":
        return podDSessions;
      case "podE":
        return podESessions;
      case "podF":
        return podFSessions;
      case "alacarte":
        return alacarteSessions;
      case "mySessions":
        return myPodSessions;
      default:
        return allPodSessions;
    }
  }, [
    selectedPod,
    allPodSessions,
    myPodSessions,
    podASessions,
    podBSessions,
    podCSessions,
    podDSessions,
    podESessions,
    podFSessions,
    alacarteSessions,
  ]);

  const displayedSessions = queueSessions;
  const viewingQueue = !["mySessions", "secondaries", "collegeBoard"].includes(
    selectedPod,
  );
  const urgentSessions = useMemo(
    () => (viewingQueue ? urgentSessionsFromDisplayed(displayedSessions) : []),
    [viewingQueue, displayedSessions],
  );
  const showUrgentQueue = useMemo(
    () => shouldShowUrgentPanel(urgentSessions, claimedIds),
    [urgentSessions, claimedIds],
  );

  useEffect(() => {
    if (!showUrgentQueue || !compactQueue) setUrgentOpen(false);
  }, [showUrgentQueue, compactQueue]);

  const handleClaimSession = useCallback(
    async (session) => {
      const sessionId = session?.id;
      if (!sessionId || claimingId) return;

      setClaimingId(sessionId);
      setClaimNotice(null);

      const navigateToSession = () => {
        window.open(`/pod-session-v2?id=${sessionId}`, "_blank");
      };

      try {
        const claimState = await fetchPodSessionClaimState(sessionId);
        if (claimState && !isUnclaimedProctor(claimState.proctor)) {
          const claimEvent = { type: "claim", id: sessionId };
          queueEventsRef.current.push(claimEvent);
          rememberQueueEvent(claimEvent);
          setClaimNotice(
            `Claimed by ${claimState.proctor || "another proctor"}`,
          );
          return;
        }

        if (!proctorUsername) {
          setClaimNotice("Unable to claim this session.");
          return;
        }

        const { notificationError } = await claimPodSessionAndNotify(
          sessionId,
          proctorUsername,
          { expectedProctor: claimState?.proctor },
        );
        const claimEvent = { type: "claim", id: sessionId };
        queueEventsRef.current.push(claimEvent);
        rememberQueueEvent(claimEvent);
        if (notificationError) {
          setClaimNotice(
            "Session claimed, but other proctors were not notified.",
          );
        }
        navigateToSession();
      } catch (error) {
        if (error instanceof PodSessionAlreadyClaimedError) {
          const winner = error.session?.proctor || "another proctor";
          const claimEvent = { type: "claim", id: sessionId };
          queueEventsRef.current.push(claimEvent);
          rememberQueueEvent(claimEvent);
          setClaimNotice(`Claimed by ${winner}`);
          return;
        }
        console.error("Error claiming pod session:", error);
        setClaimNotice("Unable to claim this session.");
      } finally {
        setClaimingId(null);
      }
    },
    [claimingId, proctorUsername, rememberQueueEvent],
  );

  const columns = useMemo(
    () =>
      createColumns({
        showTransferredIndicator: selectedPod === "mySessions",
        showCreatedAtCentralDate: selectedPod === "mySessions",
        claimAction: selectedPod !== "mySessions",
        compactColumns:
          selectedPod !== "mySessions" && !compactQueue && showUrgentQueue,
        claimingId,
        claimedIds,
        notTawkOrganizationIds,
        onClaimSession: handleClaimSession,
        proctorUsernames: mySessionsProctorUsernames,
      }),
    [
      selectedPod,
      mySessionsProctorUsernames,
      compactQueue,
      showUrgentQueue,
      claimingId,
      claimedIds,
      notTawkOrganizationIds,
      handleClaimSession,
    ],
  );
  const highlightedColumns = useMemo(
    () => withQueueRowHighlight(columns, notTawkOrganizationIds),
    [columns, notTawkOrganizationIds],
  );

  const claimedKey = claimedIdsSignature(claimedIds);
  const rowProps = useMemo(() => {
    if (selectedPod === "mySessions") {
      return displayedSessions.reduce((props, session) => {
        if (!session.id) return props;
        props[session.id] = queueRowDisplayProps(session, {
          background:
            session.status === "FINISHED"
              ? { color: "status-ok", opacity: "weak" }
              : undefined,
        });
        return props;
      }, {});
    }
    return queueRowProps(displayedSessions, claimedIds);
  }, [selectedPod, displayedSessions, claimedIds, claimedKey]);

  const dataProperties = useMemo(() => {
    const properties = {
      id: {
        label: "ID",
        property: "id",
        filter: false,
        search: false,
      },
      testerName: {
        label: "Tester Name",
        property: "testerName",
        filter: false,
      },
      testerEmail: {
        label: "Tester Email",
        property: "testerEmail",
        filter: false,
      },
      organizationName: {
        label: "Organization Name",
        property: "organizationName",
        filter: true,
      },
    };

    if (selectedPod === "mySessions") {
      properties.createdAtCentralDate = {
        label: "Date",
        property: "createdAtCentralDate",
        filter: false,
      };
    }

    return properties;
  }, [selectedPod]);

  const fetchAllPodSessions = useCallback(async () => {
    if (queueFetchRef.current) {
      queueReconcileAgainRef.current = true;
      return queueFetchRef.current;
    }

    const run = async () => {
      setLoading(true);
      queueEventsRef.current = [];
      const pages = [];
      let token = null;

      try {
        do {
          const { data } = await API.graphql({
            query: PodQueueSessionsByStatus,
            variables: {
              status: "QUEUE",
              sortDirection: "ASC",
              nextToken: token,
              limit: 300,
            },
            authMode: "AMAZON_COGNITO_USER_POOLS",
          });

          pages.push(data.podSessionByStatus?.items ?? []);
          token = data.podSessionByStatus?.nextToken ?? null;
        } while (token);

        const snapshot = queueStateRef.current;
        if (snapshot.loaded) {
          const confirmed = await readMissingQueueSessions(
            snapshot.sessions,
            pages,
            async (id) => {
              const { data } = await API.graphql({
                query: GetPodSession,
                variables: { id },
                authMode: "AMAZON_COGNITO_USER_POOLS",
              });
              return data?.getPodSession ?? null;
            },
          );
          if (confirmed.length) pages.push(confirmed);
        }

        const duringFetch = queueEventsRef.current.slice();
        queueEventsRef.current = queueEventsRef.current.slice(
          duringFetch.length,
        );
        const trailing = queueEventsRef.current.splice(0);
        const events = duringFetch.concat(trailing);
        setQueueState((current) => replayQueueEvents(current, pages, events));
      } catch (err) {
        console.error("Error fetching pod sessions:", err);
      } finally {
        setLoading(false);
      }
    };

    const promise = run().finally(() => {
      queueFetchRef.current = null;
      if (queueReconcileAgainRef.current) {
        queueReconcileAgainRef.current = false;
        fetchAllPodSessions();
      }
    });
    queueFetchRef.current = promise;
    return promise;
  }, [rememberQueueEvent]);

  useEffect(() => {
    fetchAllPodSessions();
  }, [fetchAllPodSessions]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    let cancelled = false;

    fetchOrCreateUser()
      .then(({ user, email }) => {
        if (cancelled) return;
        setProctorUsername(getProctorUsername(user, email));
      })
      .catch((error) => {
        console.error("Error loading proctor identity:", error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let connectionDisrupted = false;

    const record = (event) => {
      if (event?.claimGeneration == null) queueEventsRef.current.push(event);
      rememberQueueEvent(event);
    };

    const restartReconcile = () => {
      fetchAllPodSessions();
    };

    const createSubscription = bindGraphqlSubscription({
      graphql: API.graphql.bind(API),
      operation: {
        query: onLitePodSessionCreate,
        variables: { type: "PodSession" },
      },
      onNext: (data) => {
        const session = data.value?.data?.onLitePodSessionCreate;
        if (!session?.id) return;
        record({ type: "create", session });
      },
      onRestart: restartReconcile,
    });

    const claimSubscription = bindGraphqlSubscription({
      graphql: API.graphql.bind(API),
      operation: { query: onPodSessionClaim },
      onNext: (data) => {
        const sessionId = data.value?.data?.onClaim?.id;
        if (!sessionId) return;
        record({ type: "claim", id: sessionId });
      },
      onRestart: restartReconcile,
    });

    const urgentSubscription = bindGraphqlSubscription({
      graphql: API.graphql.bind(API),
      operation: { query: onPopToTopOfPod },
      onNext: (data) => {
        const sessionId = data.value?.data?.onPopToTopOfPod?.id;
        if (!sessionId) return;
        record({ type: "urgent", id: sessionId });
      },
    });

    const updateSubscription = bindGraphqlSubscription({
      graphql: API.graphql.bind(API),
      operation: { query: onUpdatePodSession },
      onNext: (data) => {
        const session = data.value?.data?.onUpdatePodSession;
        if (!session?.id) return;
        record({ type: "update", session });
      },
      onRestart: restartReconcile,
    });

    const removeHubListener = Hub.listen("api", ({ payload }) => {
      if (payload.event !== "ConnectionStateChange") return;
      const state = payload.data?.connectionState;
      if (
        state === "ConnectionDisrupted" ||
        state === "ConnectionDisruptedPendingNetwork"
      ) {
        connectionDisrupted = true;
        return;
      }
      if (state === "Connected" && connectionDisrupted) {
        connectionDisrupted = false;
        fetchAllPodSessions();
      }
    });

    return () => {
      createSubscription.unsubscribe();
      claimSubscription.unsubscribe();
      urgentSubscription.unsubscribe();
      updateSubscription.unsubscribe();
      removeHubListener();
    };
  }, [fetchAllPodSessions, rememberQueueEvent]);

  return (
    <Box fill={true} pad="medium">
      <QueueRowHighlightStyle />
      <Box
        background="#FFD08A"
        pad={{ horizontal: "medium", vertical: "xsmall" }}
        round="small"
        margin={{ bottom: "small" }}
      >
        <Text size="small" textAlign="center">
          Rows highlighted orange are Pod D - Priority
        </Text>
      </Box>
      <Box fill="horizontal" margin={{ bottom: "medium" }}>
        {compactQueue ? (
          <Box direction="column" gap="small" align="stretch" width="100%">
            <Box gap="xsmall" width="100%">
              <Text weight="normal" size="small" color="dark-3">
                Select Pod Session
              </Text>
              <Select
                size="small"
                options={podDropdownOptions}
                labelKey="label"
                valueKey={{ key: "value", reduce: true }}
                value={
                  podDropdownOptions.some(
                    (option) => option.value === selectedPod,
                  )
                    ? selectedPod
                    : undefined
                }
                placeholder="Select pod group"
                onChange={({ value }) => handlePodDropdownChange(value)}
                style={{
                  ...controlHeightStyle,
                  display: "flex",
                  width: "100%",
                }}
              />
            </Box>
            <Button
              label="Add Session"
              icon={addSessionIcon}
              gap="xsmall"
              primary
              onClick={handleOpenAddSessionModal}
              pad={{ vertical: "0", horizontal: "12px" }}
              style={{ ...buttonControlStyle, alignSelf: "flex-start" }}
            />
            {showUrgentQueue && (
              <UrgentQueueButton
                onClick={() => setUrgentOpen(true)}
                style={{ ...buttonControlStyle, alignSelf: "flex-start" }}
              />
            )}
          </Box>
        ) : (
          <Box
            direction="row"
            fill="horizontal"
            justify="between"
            align="end"
            wrap
            gap="small"
          >
            <Box direction="row" gap="small" align="end" wrap>
              <Box gap="xsmall">
                <Text weight="normal" size="small" color="dark-3">
                  Select Pod Session
                </Text>
                <Box width="small" style={{ maxWidth: "100%" }}>
                  <Select
                    size="small"
                    options={podDropdownOptions}
                    labelKey="label"
                    valueKey={{ key: "value", reduce: true }}
                    value={
                      podDropdownOptions.some(
                        (option) => option.value === selectedPod,
                      )
                        ? selectedPod
                        : undefined
                    }
                    placeholder="Select pod group"
                    onChange={({ value }) => handlePodDropdownChange(value)}
                    style={{
                      ...controlHeightStyle,
                      display: "flex",
                      width: "100%",
                    }}
                  />
                </Box>
              </Box>
              <Button
                label="My Sessions"
                icon={
                  selectedPod === "mySessions"
                    ? mySessionsIconActive
                    : mySessionsIcon
                }
                gap="xsmall"
                primary={selectedPod === "mySessions"}
                onClick={handleMySessions}
                disabled={loadingMySessions}
                pad={{ vertical: "0", horizontal: "12px" }}
                style={buttonControlStyle}
              />
              <Button
                label="Add Session"
                icon={addSessionIcon}
                gap="xsmall"
                primary
                onClick={handleOpenAddSessionModal}
                pad={{ vertical: "0", horizontal: "12px" }}
                style={buttonControlStyle}
              />
            </Box>
            <Box direction="row" gap="small" align="end" wrap>
              <Button
                label="Secondaries"
                icon={
                  selectedPod === "secondaries"
                    ? secondariesSessionIconActive
                    : secondariesSessionIcon
                }
                gap="xsmall"
                primary={selectedPod === "secondaries"}
                onClick={openSecondaries}
                pad={{ vertical: "0", horizontal: "12px" }}
                style={buttonControlStyle}
              />
              <Button
                label="College Board"
                icon={
                  selectedPod === "collegeBoard"
                    ? mySessionsIconActive
                    : mySessionsIcon
                }
                gap="xsmall"
                primary={selectedPod === "collegeBoard"}
                onClick={openCollegeBoard}
                pad={{ vertical: "0", horizontal: "12px" }}
                style={buttonControlStyle}
              />
            </Box>
          </Box>
        )}
      </Box>
      {claimNotice && (
        <Box margin={{ bottom: "small" }}>
          <Text color="status-warning">{claimNotice}</Text>
        </Box>
      )}
      {showAddSessionModal && (
        <Layer
          onEsc={handleCloseAddSessionModal}
          onClickOutside={handleCloseAddSessionModal}
        >
          <Box pad="medium" gap="medium" width="medium">
            <Text weight="bold" size="large">
              Add Session
            </Text>
            <Form
              value={formValues}
              onChange={(nextValue) => setFormValues(nextValue)}
              onSubmit={handleCreateSession}
              onReset={handleCloseAddSessionModal}
            >
              <FormField label="Organization" name="organization" required>
                <Select
                  name="organization"
                  placeholder="Select organization"
                  options={organizationOptions}
                  labelKey="name"
                  valueKey="id"
                  onSearch={handleOrganizationSearch}
                  searchPlaceholder="Search organizations"
                  disabled={loadingOrganizations}
                />
              </FormField>
              <FormField label="Tester Name" name="testerName" required>
                <TextInput name="testerName" />
              </FormField>
              <FormField label="Tester Email" name="testerEmail" required>
                <TextInput name="testerEmail" type="email" />
              </FormField>
              <FormField label="Tester Phone" name="testerPhone">
                <TextInput name="testerPhone" type="tel" />
              </FormField>
              {createSessionError && (
                <Text size="small" color="status-critical">
                  {createSessionError}
                </Text>
              )}
              <Box direction="row" gap="small" justify="end">
                <Button label="Cancel" type="reset" />
                <Button
                  label="Create Session"
                  type="submit"
                  primary
                  disabled={submittingSession || loadingOrganizations}
                />
              </Box>
            </Form>
          </Box>
        </Layer>
      )}
      {secondariesMounted && (
        <Box
          style={{
            display: selectedPod === "secondaries" ? undefined : "none",
          }}
        >
          <PodQueueSecondaries />
        </Box>
      )}
      {collegeBoardMounted && (
        <Box
          style={{
            display: selectedPod === "collegeBoard" ? undefined : "none",
          }}
        >
          <PodQueueCollegeBoard
            visible={selectedPod === "collegeBoard"}
            notTawkOrganizationIds={notTawkOrganizationIds}
          />
        </Box>
      )}
      {compactQueue && (
        <UrgentQueuePopup
          open={urgentOpen}
          onClose={() => setUrgentOpen(false)}
          sessions={urgentSessions}
          claimedIds={claimedIds}
          claimingId={claimingId}
          notTawkOrganizationIds={notTawkOrganizationIds}
          onClaimSession={handleClaimSession}
        />
      )}
      {!["secondaries", "collegeBoard"].includes(selectedPod) && (
        <Box
          direction={compactQueue ? "column" : "row"}
          gap={!compactQueue && showUrgentQueue ? "small" : "medium"}
          fill="horizontal"
          align="start"
        >
          <Box
            flex={{ grow: 1, shrink: 1 }}
            style={{ minWidth: 0 }}
            fill="horizontal"
            overflow={{ horizontal: "auto" }}
          >
            <Data data={displayedSessions} properties={dataProperties}>
              <Toolbar align="center" margin={{ bottom: "small" }}>
                <DataSearch
                  placeholder="Search"
                  style={{
                    height: CONTROL_HEIGHT,
                    minHeight: CONTROL_HEIGHT,
                    maxHeight: CONTROL_HEIGHT,
                    boxSizing: "border-box",
                  }}
                />
                <DataSummary />
              </Toolbar>
              {selectedPod === "mySessions" ? (
                <DataTable
                  columns={highlightedColumns}
                  primaryKey="id"
                  rowProps={rowProps}
                />
              ) : (
                <QueueDataTable
                  sessions={displayedSessions}
                  claimedIds={claimedIds}
                  columns={columns}
                  rowProps={rowProps}
                  notTawkOrganizationIds={notTawkOrganizationIds}
                  useFilteredData
                  pad={
                    !compactQueue && showUrgentQueue
                      ? queueCompactTablePad
                      : undefined
                  }
                />
              )}
            </Data>
          </Box>
          {!compactQueue && showUrgentQueue && (
            <UrgentQueuePanel
              sessions={urgentSessions}
              claimedIds={claimedIds}
              claimingId={claimingId}
              notTawkOrganizationIds={notTawkOrganizationIds}
              onClaimSession={handleClaimSession}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
